import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Drawer, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import WorkOutlinedIcon from '@mui/icons-material/WorkOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { AppButton } from '../../../components/UI';
import { ConfirmDialog, EmptyState, ErrorBanner, LinkedinBlockedHelp, ListToolbar, Pager, Panel, PanelHeader, PipelineSteps, SectionTitle, Tags } from './ui';
import { CsvUpload } from './CandidateViews';
import { deleteMission, deleteMissionOperation, fetchMissionOperations, fetchSourcedJobs, isLinkedinBlocked, retryMissionSmart, subscribeEvents } from '../api';
import { formatDate } from '../constants';

const headSx = { bgcolor: '#f8fafc', color: '#64748b', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', py: 1, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const rowSx = { cursor: 'pointer', '& td': { borderBottom: '1px solid #f1f5f9', py: 1.1 } };

const MissionCell = ({ title, sub }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
    <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <WorkOutlinedIcon sx={{ fontSize: 17 }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a' }}>{title}</Typography>
      <Typography noWrap sx={{ fontSize: '0.74rem', color: '#64748b' }}>{sub || '—'}</Typography>
    </Box>
  </Box>
);

const Field = ({ label, value }) => (
  <Box sx={{ p: 1.25, borderRadius: '10px', bgcolor: '#f8fbfe', border: '1px solid #e2edf7', minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b' }}>{label}</Typography>
    <Typography noWrap title={value || ''} sx={{ fontSize: '0.84rem', fontWeight: 700, color: value ? '#0f172a' : '#cbd5e1', mt: 0.25 }}>{value || 'Non renseigné'}</Typography>
  </Box>
);

const MissionDrawer = ({ item, onClose, onDelete }) => {
  const [copied, setCopied] = useState(false);
  if (!item) return null;
  const url = item.linkedin_url;
  return (
    <Drawer anchor="right" open={!!item} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 560 }, display: 'flex', flexDirection: 'column' } } }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #eef2f7', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 46, height: 46, borderRadius: '12px', background: 'linear-gradient(135deg, #0556a4, #03407b)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <WorkOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>{item.title || 'Titre indisponible'}</Typography>
          <Typography noWrap sx={{ fontSize: '0.78rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 15 }} />{[item.company, item.location].filter(Boolean).join(' · ') || 'Localisation inconnue'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {url && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, pl: 1.25, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Typography noWrap title={url} sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem', color: '#475569' }}>{url}</Typography>
            <Tooltip title={copied ? 'Copié' : 'Copier le lien'}>
              <IconButton size="small" onClick={async () => { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#fff' }}>
                {copied ? <CheckIcon fontSize="small" sx={{ color: '#16a34a' }} /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Ouvrir l'offre LinkedIn">
              <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ borderRadius: '8px', bgcolor: '#03407b', color: '#fff', '&:hover': { bgcolor: '#02357a' } }}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
          <Field label="Entreprise" value={item.company} />
          <Field label="Contrat" value={item.employment_type} />
          <Field label="Secteur" value={item.industry} />
          <Field label="Niveau" value={item.experience_level} />
          <Field label="Expérience requise" value={item.years_of_experience_required != null ? `${item.years_of_experience_required} an(s)` : null} />
          <Field label="Formation" value={item.education_requirements} />
          <Field label="Rémunération" value={item.salary_range} />
          <Field label="Publiée" value={item.posted} />
        </Box>
        <Box><SectionTitle>Compétences techniques</SectionTitle><Tags values={item.technical_skills} /></Box>
        <Box><SectionTitle>Langages de programmation</SectionTitle><Tags values={item.programming_languages} color="#0556a4" bg="#f0f7fd" /></Box>
        <Box><SectionTitle>Langues</SectionTitle><Tags values={item.spoken_languages} color="#16a34a" bg="#f0fdf4" /></Box>
        <Box><SectionTitle>Certifications</SectionTitle><Tags values={item.certifications} color="#7c3aed" bg="#f5f3ff" /></Box>
        {item.summary && (
          <Box>
            <SectionTitle>Résumé</SectionTitle>
            <Typography sx={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{item.summary}</Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8' }}>Stockée le {formatDate(item.stored_at)}</Typography>
      </Box>

      {onDelete && (
        <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eef2f7', bgcolor: '#fbfdff' }}>
          <AppButton fullWidth variant="danger" startIcon={<DeleteOutlinedIcon />} onClick={() => onDelete(item)}>Supprimer du sourcing</AppButton>
        </Box>
      )}
    </Drawer>
  );
};

export const ImportMissionsView = ({ op, onLaunch, onReset, onGoTo }) => (
  <Panel>
    <PanelHeader icon={<CloudUploadOutlinedIcon />} title="Importer des missions depuis LinkedIn" subtitle="CSV d'URL d'offres LinkedIn, structurées au format mission puis contrôlées avant stockage." />
    {op.phase === 'idle' || (op.phase === 'error' && !op.jobId) ? (
      <>
        <CsvUpload
          title="Fichier CSV des offres"
          hint="colonne linkedin_job_url (ou une colonne d'URL)"
          limitLabel="Missions à traiter"
          launchLabel="Collecter, structurer et stocker"
          onLaunch={(file, limit) => onLaunch(file, 'jobs_import', limit)}
        />
        {op.error && <Box sx={{ px: 2.5, pb: 2.5 }}>{isLinkedinBlocked(op.error) ? <LinkedinBlockedHelp detail={op.error} /> : <ErrorBanner>{op.error}</ErrorBanner>}</Box>}
      </>
    ) : (
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', mb: 2 }}>
          {op.phase === 'running' ? `Import en cours : ${op.fileName}` : op.phase === 'done' ? 'Import terminé' : "L'import s'est arrêté"}
        </Typography>
        <PipelineSteps steps={op.steps} stepStates={op.stepStates} progress={op.progress} logs={op.logs} error={op.error} />
        {op.phase !== 'running' && (
          <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {op.summary && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#16a34a', mr: 'auto' }}>
                <CheckCircleOutlinedIcon fontSize="small" />
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>{op.summary}</Typography>
              </Box>
            )}
            <AppButton variant="secondary" onClick={onReset}>Nouvel import</AppButton>
            <AppButton startIcon={<WorkOutlinedIcon />} onClick={() => onGoTo('stored')}>Voir les missions</AppButton>
          </Box>
        )}
        {op.phase === 'running' && (
          <Typography sx={{ mt: 1.5, fontSize: '0.74rem', color: '#94a3b8' }}>Vous pouvez changer d'onglet : la collecte continue en arrière-plan.</Typography>
        )}
      </Box>
    )}
  </Panel>
);

export const MissionsView = ({ snackbarShowMessage, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('title');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [opened, setOpened] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchSourcedJobs().then((r) => setItems(r.items || [])).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('fr');
    const list = items.filter((m) => !needle || [m.title, m.company, m.location, m.employment_type, m.industry, ...(m.technical_skills || []), ...(m.programming_languages || [])]
      .filter((v) => typeof v === 'string').join(' ').toLocaleLowerCase('fr').includes(needle));
    return [...list].sort((a, b) => (sort === 'date_desc'
      ? String(b.stored_at || '').localeCompare(String(a.stored_at || ''))
      : String(a.title || '').localeCompare(String(b.title || ''), 'fr')));
  }, [items, query, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * pageSize, current * pageSize);

  const remove = async () => {
    setBusy(true);
    try {
      await deleteMission(toDelete.uuid, reason.trim());
      setItems((v) => v.filter((x) => x.uuid !== toDelete.uuid));
      snackbarShowMessage('Mission supprimée du sourcing.', 'success');
      setToDelete(null);
      onChanged?.();
    } catch (e) {
      snackbarShowMessage(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <PanelHeader icon={<WorkOutlinedIcon />} tone="green" title="Missions sourcées" subtitle="Offres LinkedIn structurées et vectorisées, prêtes pour le matching avec vos candidats." />
      {error && <Box sx={{ px: 2, pt: 2 }}><ErrorBanner>{error}</ErrorBanner></Box>}
      <ListToolbar
        query={query}
        onQuery={(v) => { setQuery(v); setPage(1); }}
        placeholder="Rechercher un titre, une entreprise, une compétence ou un lieu…"
        sort={sort}
        onSort={setSort}
        sortOptions={[{ value: 'title', label: 'Titre A–Z' }, { value: 'date_desc', label: 'Plus récent' }]}
        count={filtered.length}
      />
      {loading ? (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rounded" height={46} />)}</Box>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<WorkOutlinedIcon />} title={query ? 'Aucun résultat' : 'Aucune mission sourcée'} text={query ? 'Essayez un autre mot-clé.' : "Importez un CSV d'offres LinkedIn pour alimenter le sourcing."} />
      ) : (
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, width: '32%' }}>Mission</TableCell>
                <TableCell sx={headSx}>Compétences</TableCell>
                <TableCell sx={{ ...headSx, width: 130 }}>Contrat</TableCell>
                <TableCell sx={{ ...headSx, width: 110 }}>Stockée le</TableCell>
                <TableCell sx={{ ...headSx, width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((m) => (
                <TableRow key={m.uuid} hover onClick={() => setOpened(m)} sx={rowSx}>
                  <TableCell><MissionCell title={m.title || 'Sans titre'} sub={[m.company, m.location].filter(Boolean).join(' · ')} /></TableCell>
                  <TableCell><Tags values={[...(m.technical_skills || []), ...(m.programming_languages || [])]} max={3} nowrap empty="—" /></TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: '#475569' }}>{m.employment_type || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>{formatDate(m.stored_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Voir la fiche">
                      <IconButton size="small" onClick={() => setOpened(m)} sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px' }}><VisibilityOutlinedIcon sx={{ fontSize: 17 }} /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!loading && filtered.length > 0 && <Pager page={current} pages={pages} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />}

      <MissionDrawer item={opened} onClose={() => setOpened(null)} onDelete={(m) => { setOpened(null); setToDelete(m); setReason(''); }} />
      <ConfirmDialog
        open={!!toDelete}
        title="Supprimer cette mission ?"
        text={`« ${toDelete?.title || 'Cette mission'} » sera retirée uniquement du sourcing. Les missions officielles du CRM ne sont pas touchées.`}
        confirmLabel="Supprimer"
        danger
        busy={busy}
        withReason
        reason={reason}
        onReason={setReason}
        onConfirm={remove}
        onClose={() => setToDelete(null)}
      />
    </Panel>
  );
};

export const MissionOperationsView = ({ kind, snackbarShowMessage, onChanged }) => {
  const failed = kind === 'failed';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    if (!failed) {
      const r = await fetchMissionOperations('not_storable');
      setItems(r.items || []);
      return;
    }
    const [a, b] = await Promise.all([fetchMissionOperations('extraction_failed'), fetchMissionOperations('storage_failed')]);
    setItems([...(a.items || []), ...(b.items || [])]);
  }, [failed]);

  useEffect(() => {
    setLoading(true);
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const retry = async (url) => {
    setBusy(`retry:${url}`);
    setError('');
    try {
      const result = await retryMissionSmart([url]);
      const opId = result.scraping_operation?.job_id;
      if (opId) {
        snackbarShowMessage('Reprise de la mission lancée.', 'success');
        subscribeEvents(opId, (event) => {
          if (event.type === 'close') { load().catch((e) => setError(e.message)); onChanged?.(); }
        });
      } else {
        await load();
        onChanged?.();
        snackbarShowMessage('Reprise du stockage terminée.', 'success');
      }
    } catch (e) {
      setError(e.message);
      snackbarShowMessage(e.message, 'error');
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    const url = toDelete.linkedin_url;
    setBusy(`delete:${url}`);
    try {
      await deleteMissionOperation(url, reason.trim());
      setItems((v) => v.filter((x) => x.linkedin_url !== url));
      setToDelete(null);
      onChanged?.();
      snackbarShowMessage('Mission retirée de la liste.', 'success');
    } catch (e) {
      snackbarShowMessage(e.message, 'error');
    } finally {
      setBusy('');
    }
  };

  return (
    <Panel>
      <PanelHeader
        icon={failed ? <ReplayIcon /> : <ShieldOutlinedIcon />}
        tone={failed ? 'blue' : 'amber'}
        title={failed ? 'File de relance missions' : 'Missions non stockables'}
        subtitle={failed ? 'Erreurs techniques : la relance recollecte ou restocke la mission.' : 'Missions refusées par le contrôle factuel, avec leur justification.'}
      />
      {error && <Box sx={{ px: 2, pt: 2 }}><ErrorBanner>{error}</ErrorBanner></Box>}
      {loading ? (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={46} />)}</Box>
      ) : items.length === 0 ? (
        <EmptyState icon={failed ? <ReplayIcon /> : <ShieldOutlinedIcon />} title={failed ? 'File vide' : 'Aucune mission rejetée'} text={failed ? 'Aucune mission en échec technique.' : 'Toutes les missions importées ont passé le contrôle.'} />
      ) : (
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, width: '34%' }}>Mission</TableCell>
                <TableCell sx={headSx}>Raison</TableCell>
                <TableCell sx={{ ...headSx, width: failed ? 64 : 100 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.linkedin_url} sx={{ '& td': { borderBottom: '1px solid #f1f5f9', py: 1.1 } }}>
                  <TableCell><MissionCell title={item.title || 'Mission non structurée'} sub={[item.company, item.location].filter(Boolean).join(' · ')} /></TableCell>
                  <TableCell>
                    <Typography title={item.reason_detail || ''} sx={{ fontSize: '0.78rem', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.reason_detail || item.reason_code || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Relancer la mission">
                        <span>
                          <IconButton size="small" disabled={!!busy} onClick={() => retry(item.linkedin_url)} sx={{ color: '#03407b', border: '1px solid #cfe0f1', borderRadius: '8px' }}>
                            <ReplayIcon sx={{ fontSize: 17, animation: busy === `retry:${item.linkedin_url}` ? 'src-spin 1s linear infinite' : 'none', '@keyframes src-spin': { to: { transform: 'rotate(-360deg)' } } }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {!failed && (
                        <Tooltip title="Retirer de la liste">
                          <span>
                            <IconButton size="small" disabled={!!busy} onClick={() => { setToDelete(item); setReason(''); }} sx={{ color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px' }}>
                              <DeleteOutlinedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmDialog
        open={!!toDelete}
        title="Retirer cette mission ?"
        text="La mission sera retirée de la liste de contrôle. Aucune mission officielle n'est modifiée."
        confirmLabel="Retirer"
        danger
        busy={busy.startsWith('delete:')}
        withReason
        reason={reason}
        onReason={setReason}
        onConfirm={remove}
        onClose={() => setToDelete(null)}
      />
    </Panel>
  );
};
